import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Box,
    FormGroup,
    FormHelperText,
    Alert,
    AlertColor,
    Autocomplete,
    Chip,
} from "@mui/material";
import { Formik, Form } from "formik";
import axiosInstance from "../../../services/axios-instance";
import TableOrganizations, { organizationNameSchema, getUnassignedClients } from "../../elements/TableOrganizations";
import { Organization } from "../../../interface/organizationInterface";
import { Client } from "../../../interface/clientInterface";
import axios from "axios";

const AdminOrganizations: React.FC = () => {
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [allClients, setAllClients] = useState<Client[]>([]);
    const [selectedClients, setSelectedClients] = useState<Client[]>([]);
    const [alertMessage, setAlertMessage] = useState("");
    const [severity, setSeverity] = useState<AlertColor | undefined>("info");

    useEffect(() => {
        fetchOrganizations();
        fetchClients();
    }, []);

    const fetchOrganizations = async () => {
        try {
            const response = await axiosInstance.get("/organizations");
            setOrganizations(response.data.data);
        } catch (error) {
            console.error("Error fetching organizations:", error);
        }
    };

    const fetchClients = async () => {
        try {
            const response = await axiosInstance.get("/clients");
            setAllClients(response.data.data);
        } catch (error) {
            console.error("Error fetching clients:", error);
        }
    };

    const addOrganization = async (values: { name: string }, { resetForm }: any) => {
        try {
            await axiosInstance.post("/organizations", {
                name: values.name,
                client_ids: selectedClients.map(c => c.id),
            });
            setSeverity("success");
            setAlertMessage("Organization added successfully");
            resetForm();
            setSelectedClients([]);
            fetchOrganizations();
        } catch (error) {
            setSeverity("error");
            setAlertMessage(
                axios.isAxiosError(error) ? error.response?.data.message : "Error adding organization"
            );
        }
    };

    return (
        <div>
            <Box maxWidth="500px" mx="auto">
                <h2 style={{ marginBottom: "0.5rem" }}>Add Organization</h2>
                <Formik
                    initialValues={{ name: "" }}
                    validationSchema={organizationNameSchema}
                    onSubmit={addOrganization}
                >
                    {({ handleSubmit, handleChange, values, errors, touched }) => (
                        <Form onSubmit={handleSubmit}>
                            <FormGroup>
                                <TextField
                                    label="Organization Name"
                                    name="name"
                                    value={values.name}
                                    onChange={handleChange}
                                    error={touched.name && Boolean(errors.name)}
                                />
                                {errors.name && touched.name && (
                                    <FormHelperText>{errors.name}</FormHelperText>
                                )}
                            </FormGroup>
                            <Box mt={2}>
                                <Autocomplete
                                    multiple
                                    options={getUnassignedClients(allClients, organizations)}
                                    getOptionLabel={(option) => option.name}
                                    value={selectedClients}
                                    onChange={(_, newValue) => setSelectedClients(newValue)}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                label={option.name}
                                                {...getTagProps({ index })}
                                                key={option.id}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Assign Clients"
                                            placeholder="Select clients"
                                        />
                                    )}
                                />
                            </Box>
                            <Box mt={2}>
                                <Button variant="contained" type="submit">
                                    Add Organization
                                </Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
                <Box sx={{ mt: "1rem" }}>
                    {alertMessage !== "" && (
                        <Alert
                            onClick={() => setAlertMessage("")}
                            variant="filled"
                            severity={severity}
                        >
                            {alertMessage}
                        </Alert>
                    )}
                </Box>
            </Box>
            {organizations.length === 0 ? (
                <Box marginTop={4}>
                    <p>No organizations found.</p>
                </Box>
            ) : (
                <Box marginTop={4}>
                    <TableOrganizations
                        organizations={organizations}
                        setOrganizations={setOrganizations}
                        allClients={allClients}
                    />
                </Box>
            )}
        </div>
    );
};

export default AdminOrganizations;
